import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Discount checkbox in the cart and on the product page (snippets/wd-cart-discount-checkbox.liquid).
 *
 * Ticking applies a code; unticking removes it. Codes already on the cart are kept, with one
 * exception: ticking a daily-offer code drops any other daily-offer code still on the cart (the
 * bar's `offerCodes` lists them), because yesterday's code stays valid for hours after the bar
 * stops showing it and two days' codes would stack. A code the customer typed is never touched,
 * and a refused tick puts the cart back as it was. Shopify's `discount` field replaces the whole
 * list. The write follows the cart pipeline (CLAUDE.md R2):
 * `sections` from every cart-items-component, then window.manualCartRefresh, which morphs the
 * drawer, cart page and totals, and redraws this box from the cart itself.
 *
 * Which code: the running daily offer's, when the bar (<wd-daily-offer>,
 * sections/wd-daily-offer.liquid) shows an entry with a code and this box has data-follow-offer;
 * else the settings code in the box's state. The bar exposes `activeOffer` and dispatches
 * 'wd:daily-offer' on document with `detail.offer` (null when nothing runs). For an offer the box
 * rebuilds its texts from the templates in its data attributes (createElement and textContent,
 * never HTML strings), shows itself to everyone, and runs the "ENDS IN" countdown (<wd-countdown>,
 * defined by assets/wd-daily-offer.js, which the bar section loads). All of this is redone after
 * every cart redraw, because the server markup is always the settings version. Two things make
 * that work: the morph calls updatedCallback() on the parsed COPY of the new markup, not on the
 * live box, so that copy asks the bar for the running offer itself before it is drawn; and after
 * the cart:update that carried the morph, the live box applies the offer once more on the next
 * frame, in case the morph kept it in place. With no offer and no settings code the box hides.
 *
 * On the product page (data-context="pdp", blocks/wd-pdp-discount-checkbox.liquid) the box is not
 * in any cart section, so the refresh does not redraw it: it reads the cart's codes from /cart.js
 * when it connects and on every cart:update / cart:updated. There the cart is often empty, and a
 * code on an empty cart never applies, so a tick on an empty cart keeps the code on the cart
 * (Shopify applies it once a print is added) instead of taking it off with the message.
 * While a daily offer runs, a product page box with data-product-offer-codes ("|wrap10|fav10|") hides
 * unless the running code is in that list: the checkbox shows only on the offer's products.
 *
 * @typedef {{ code: string, codes: string[] }} State
 * @typedef {{ code: string, headline: string, end: number }} Offer
 * @typedef {{ state: HTMLScriptElement, input: HTMLInputElement, labelOff?: HTMLElement, labelOn?: HTMLElement, noteOff?: HTMLElement, ends?: HTMLElement }} Refs
 *
 * @extends {Component<Refs>}
 */
/**
 * A "does not apply" message waiting for the box to be drawn again. The cart refresh replaces the
 * box's markup after the request, so the message is shown when the box connects or is updated
 * (morph), for a few seconds, instead of on a timer that the redraw would undo.
 * @type {{ message: string, until: number } | null}
 */
let pendingError = null;
const ERROR_MS = 8000;

/**
 * The running daily offer as this box needs it, or null when it has no code.
 * @param {unknown} value
 * @returns {Offer | null}
 */
function codeOffer(value) {
  if (!value || typeof value !== 'object') return null;
  const { code, headline, end } = /** @type {{ code?: unknown, headline?: unknown, end?: unknown }} */ (value);
  const trimmed = String(code || '').trim();
  if (!trimmed) return null;
  return { code: trimmed, headline: String(headline || ''), end: typeof end === 'number' ? end : 0 };
}

/**
 * Fills a label from a settings template, with the code in bold: "Apply my *code* discount".
 * With an offer, *offer* is replaced too (plain text): "Code: *code*. Applies in cart."
 * @param {HTMLElement} element
 * @param {string} template
 * @param {string} code
 * @param {string} [offer]
 */
function fillTemplate(element, template, code, offer) {
  element.replaceChildren();
  template.split('*code*').forEach((part, index) => {
    if (index > 0) {
      const strong = document.createElement('strong');
      strong.textContent = code;
      element.append(strong);
    }
    const text = offer === undefined ? part : part.split('*offer*').join(offer);
    if (text) element.append(document.createTextNode(text));
  });
}

/**
 * The entry's headline without its closing full stop: "Extra 10% off frames".
 * @param {string} headline
 * @returns {string}
 */
function offerLead(headline) {
  return String(headline || '').trim().replace(/[.!]+$/, '');
}

/**
 * "10% off frames. Applies in cart." from the entry's headline and the settings text.
 * @param {string} headline
 * @param {string} suffix
 * @returns {string}
 */
function offerNote(headline, suffix) {
  const lead = offerLead(headline);
  const tail = String(suffix || '').trim();
  return lead && tail ? `${lead}. ${tail}` : lead || tail;
}

/**
 * Plain text from a cart template: "Apply *offer*", "Code: *code*. Applies in cart.".
 * @param {string} template
 * @param {string} offer
 * @param {string} code
 * @returns {string}
 */
function fillText(template, offer, code) {
  return template.split('*offer*').join(offer).split('*code*').join(code);
}

class WdCartDiscountCheckbox extends Component {
  requiredRefs = ['state', 'input'];

  #busy = false;

  /** Codes on the cart, applied or not, read from /cart.js (product page only). @type {string[] | null} */
  #cartCodes = null;

  /** The running daily offer with a code, or null. @type {Offer | null} */
  #offer = null;

  /** Product page: re-read the cart. Cart: re-apply the offer after the morph has landed. */
  #onCartChange = () => {
    if (this.#onProductPage) {
      this.#syncFromCart();
      return;
    }
    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#offer = codeOffer(this.#barOffer());
      this.#applyOffer();
    });
  };

  /** @param {Event} event */
  #onOffer = (event) => {
    this.#offer = codeOffer(/** @type {CustomEvent} */ (event).detail?.offer);
    this.#applyOffer();
    if (this.#onProductPage) this.#syncFromCart();
  };

  get #onProductPage() {
    return this.dataset.context === 'pdp';
  }

  get #followsOffer() {
    return this.dataset.followOffer === 'true';
  }

  connectedCallback() {
    super.connectedCallback();
    if (this.#followsOffer) {
      document.addEventListener('wd:daily-offer', this.#onOffer);
      this.#offer = codeOffer(this.#barOffer());
    }
    this.#applyOffer();
    this.#renderError();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartChange);
    document.addEventListener('cart:updated', this.#onCartChange);
    if (this.#onProductPage) this.#syncFromCart();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('wd:daily-offer', this.#onOffer);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartChange);
    document.removeEventListener('cart:updated', this.#onCartChange);
  }

  updatedCallback() {
    super.updatedCallback();
    // Runs on the parsed copy of the new markup: ask the bar, this instance remembers nothing.
    if (this.#followsOffer) this.#offer = codeOffer(this.#barOffer());
    this.#applyOffer();
    this.#renderError();
  }

  /** The bar's running entry, or null (also when there is no bar on the page). */
  #barOffer() {
    const bar = document.querySelector('wd-daily-offer');
    return bar && 'activeOffer' in bar ? /** @type {{ activeOffer: unknown }} */ (bar).activeOffer : null;
  }

  /** Every daily-offer code the bar knows, lower-cased; empty when there is no bar. */
  #dailyCodes() {
    const bar = document.querySelector('wd-daily-offer');
    const list = bar && 'offerCodes' in bar ? /** @type {{ offerCodes: unknown }} */ (bar).offerCodes : null;
    return new Set(Array.isArray(list) ? list.map((code) => String(code).toLowerCase()) : []);
  }

  #renderError() {
    const error = this.querySelector('.wd-cd__error');
    if (!(error instanceof HTMLElement)) return;
    if (pendingError && Date.now() < pendingError.until) {
      error.textContent = pendingError.message;
      error.hidden = false;
    }
  }

  /** @returns {State | null} */
  #readState() {
    try {
      return JSON.parse(this.refs.state.textContent ?? '');
    } catch (error) {
      window.droolReportError?.('wd-cart-discount-checkbox', String(error));
      return null;
    }
  }

  /** The code this box applies: the running daily offer's, else the settings'. */
  #activeCode() {
    if (this.#offer) return this.#offer.code;
    const state = this.#readState();
    return state?.code ? String(state.code).trim() : '';
  }

  /** Draws the box for the running offer, else for the settings code, else hides it. */
  #applyOffer() {
    const state = this.#readState();
    const settingsCode = state?.code ? String(state.code).trim() : '';
    const { ends } = this.refs;
    const countdown = ends?.querySelector('wd-countdown');
    const offer = this.#offer;

    // Product page: only on the running offer's products, when the block lists the product's codes.
    const productCodes = this.dataset.productOfferCodes;
    if (offer && this.#onProductPage && productCodes !== undefined && !productCodes.includes(`|${offer.code.toLowerCase()}|`)) {
      this.hidden = true;
      return;
    }

    if (offer) {
      this.#renderTexts(offer.code, offerNote(offer.headline, this.dataset.offerNote || ''), offerLead(offer.headline));
      this.dataset.audience = 'everyone';
      this.classList.add('wd-cd--offer');
      if (ends) ends.hidden = false;
      if (countdown instanceof HTMLElement) {
        const end = String(offer.end);
        if (countdown.dataset.endsAt !== end) countdown.dataset.endsAt = end;
        /** @type {{ sync?: () => void }} */ (countdown).sync?.();
      }
      this.hidden = false;
    } else {
      this.classList.remove('wd-cd--offer');
      if (ends) ends.hidden = true;
      if (countdown instanceof HTMLElement) countdown.removeAttribute('data-ends-at');
      if (!settingsCode) {
        this.hidden = true;
        return;
      }
      this.#renderTexts(settingsCode, this.dataset.note || '', String(this.dataset.offerText || '').trim());
      this.dataset.audience = this.dataset.audienceDefault || this.dataset.audience || 'everyone';
      this.hidden = false;
    }

    // In the cart, Liquid ticked the box for the settings code; tick it for the code in use. The
    // product page reads the cart itself (#showCart).
    if (!this.#onProductPage) {
      const key = this.#activeCode().toLowerCase();
      const applied = Boolean(key) && (state?.codes ?? []).some((code) => String(code).toLowerCase() === key);
      // Property and attribute: the morph carries the attribute from the copy to the live input.
      this.refs.input.checked = applied;
      this.refs.input.toggleAttribute('checked', applied);
      this.classList.toggle('is-applied', applied);
    }
  }

  /**
   * The cart (data-cart-label, never the product page) words the unticked box from what the code
   * gives: "Apply Extra 10% off frames" / "Code: CODE. Applies in cart." Without that, or on the
   * product page, the label template and the note. The product page, while a daily offer runs,
   * uses its own templates (data-pdp-*: "Apply *offer*" / "Code: *code*. ..." / "*offer* now
   * applied"), each falling back to the above when its setting is empty.
   * @param {string} code
   * @param {string} note
   * @param {string} offerText - the daily offer's headline, or the settings' "What the code gives"
   */
  #renderTexts(code, note, offerText) {
    const { labelOff, labelOn, noteOff } = this.refs;
    const cartCopy = Boolean(offerText) && this.dataset.cartLabel !== undefined;
    const pdpOffer = this.#onProductPage && Boolean(this.#offer) && Boolean(offerText);
    const pdpLabel = pdpOffer ? this.dataset.pdpLabel || '' : '';
    const pdpNote = pdpOffer ? this.dataset.pdpNote || '' : '';
    const pdpApplied = pdpOffer ? this.dataset.pdpAppliedLabel || '' : '';
    this.classList.toggle('wd-cd--cart-copy', cartCopy);
    if (labelOff) {
      if (cartCopy) labelOff.textContent = fillText(this.dataset.cartLabel || 'Apply *offer*', offerText, code);
      else if (pdpLabel) fillTemplate(labelOff, pdpLabel, code, offerText);
      else fillTemplate(labelOff, this.dataset.labelTemplate || 'Apply my *code* discount', code);
    }
    if (labelOn) {
      if (pdpApplied) fillTemplate(labelOn, pdpApplied, code, offerText);
      else fillTemplate(labelOn, this.dataset.appliedTemplate || '*code* applied', code);
    }
    if (noteOff && pdpNote) {
      fillTemplate(noteOff, pdpNote, code, offerText);
      noteOff.hidden = false;
      return;
    }
    const text = cartCopy ? fillText(this.dataset.cartNote || '', offerText, code) : note;
    if (noteOff) {
      noteOff.textContent = text;
      noteOff.hidden = !text;
    }
  }

  async toggle() {
    const state = this.#readState();
    const { input } = this.refs;
    const code = this.#activeCode();
    if (!state || !code || this.#busy) return;

    const wanted = input.checked;
    const key = code.toLowerCase();
    const current = (this.#cartCodes ?? state.codes ?? []).filter((existing) => existing && existing.toLowerCase() !== key);
    // Ticking a daily-offer code also drops any other daily-offer code still on the cart
    // (yesterday's, valid for hours after the bar stopped showing it), never a code the customer typed.
    const stale = wanted && this.#offer ? this.#dailyCodes() : null;
    const kept = stale ? current.filter((existing) => !stale.has(existing.toLowerCase())) : current;
    const codes = wanted ? [...kept, code] : current;

    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);

    pendingError = null;
    this.#busy = true;
    this.classList.add('is-busy');
    try {
      let data = await this.#setCodes(codes, sections);

      // Shopify keeps a code that does not apply to this cart (minimum spend, wrong products) but
      // gives no discount for it. Take it off again so the box and the cart agree, and say why.
      const entry = (data.discount_codes ?? []).find((discount) => String(discount.code).toLowerCase() === key);
      const waiting = this.#onProductPage && entry && data.item_count === 0;
      const rejected = wanted && !waiting && (!entry || entry.applicable === false);
      if (rejected) data = await this.#setCodes(current, sections);

      if (this.#onProductPage) this.#showCart(data);
      // A code is not an add: on the product page the drawer refreshes but does not open.
      window.manualCartRefresh?.(data, { keepDrawerClosed: this.#onProductPage });
      if (rejected) this.#showError();
    } catch (error) {
      // Nothing changed on the cart: put the box back as it was.
      input.checked = !wanted;
      window.droolReportError?.('wd-cart-discount-checkbox', String(error));
    } finally {
      this.#busy = false;
      this.classList.remove('is-busy');
    }
  }

  /**
   * Replaces the cart's discount codes and asks for the cart sections in the same request.
   * @param {string[]} codes
   * @param {string[]} sections
   */
  async #setCodes(codes, sections) {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(','), sections: sections.join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || `HTTP ${response.status}`);
    return data;
  }

  /** Product page: reads the cart's codes and ticks the box to match. */
  async #syncFromCart() {
    if (this.#busy) return;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!this.#busy) this.#showCart(data);
    } catch (error) {
      window.droolReportError?.('wd-cart-discount-checkbox', String(error));
    }
  }

  /**
   * Product page: ticked when the code in use applies to the cart, or waits on an empty cart. Same
   * rule as the cart's box, which only counts applied codes.
   * @param {{ item_count?: number, discount_codes?: { code: string, applicable: boolean }[] }} data
   */
  #showCart(data) {
    const code = this.#activeCode();
    if (!code) return;
    const key = code.toLowerCase();
    const codes = data.discount_codes ?? [];
    const entry = codes.find((discount) => String(discount.code).toLowerCase() === key);
    const applied = Boolean(entry && (entry.applicable || data.item_count === 0));
    this.#cartCodes = codes.map((discount) => discount.code);
    this.refs.input.checked = applied;
    this.refs.input.toggleAttribute('checked', applied);
    this.classList.toggle('is-applied', applied);
  }

  /** Shows the "does not apply" message now and on the redraw that follows the cart refresh. */
  #showError() {
    const message = this.dataset.error;
    if (!message) return;
    pendingError = { message, until: Date.now() + ERROR_MS };
    for (const box of document.querySelectorAll('wd-cart-discount-checkbox')) {
      if (box instanceof WdCartDiscountCheckbox) box.#renderError();
    }
  }
}

if (!customElements.get('wd-cart-discount-checkbox')) {
  customElements.define('wd-cart-discount-checkbox', WdCartDiscountCheckbox);
}
